import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TableBody,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { common, financedEmissions } from "../../../i18n";
import DqsBadge from "../../../components/cat15/DqsBadge";
import AssetClassChip from "../../../components/cat15/AssetClassChip";
import LoanStatusChip from "../../../components/cat15/LoanStatusChip";
import {
  FeCell,
  FeHeadCell,
  FeRow,
  FeTable,
  FeTableScroll,
  FeTableWrap,
} from "../../../components/cat15/FeTable";
import FePagination from "../../../components/cat15/FePagination";
import AddLoanModal, { AddLoanForm } from "./AddLoanModal";
import alertService from "../../../components/alert/alertService";
import {
  createLoan,
  getAllLoans,
  getBorrowers,
} from "../../../service/cat15.service";
import {
  AllLoanRow,
  AssetClass,
  BorrowerListItem,
} from "../../../models/dto/cat15/cat15.types";
import { formatDate, formatNumber } from "../format";

/** All Loans tab — Figma node 6108:19985 (flat FeTable + filters + Add Loan). */
const AllLoans = () => {
  const { t } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });
  const [rows, setRows] = useState<AllLoanRow[]>([]);
  const [borrowerList, setBorrowerList] = useState<BorrowerListItem[]>([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filters
  const [search, setSearch] = useState("");
  const [assetClass, setAssetClass] = useState("all");
  const [status, setStatus] = useState("all");
  const [borrower, setBorrower] = useState("all");

  useEffect(() => {
    getAllLoans().then(setRows);
    getBorrowers().then(setBorrowerList);
  }, []);

  const parseAmount = (v: string) => Number(v.replace(/,/g, "")) || 0;

  const handleAddLoan = (values: AddLoanForm) => {
    createLoan({
      borrowerId: Number(values.borrowerId),
      assetClass: values.assetClass as AssetClass,
      loanRef: values.loanRef || undefined,
      principal: parseAmount(values.principal),
      originationDate:
        values.origination || new Date().toISOString().slice(0, 10),
      maturityDate: values.maturity || undefined,
      outstanding: values.outstanding
        ? parseAmount(values.outstanding)
        : undefined,
      asOfDate: values.year ? `${values.year}-12-31` : undefined,
    }).then(() => {
      getAllLoans().then(setRows);
      alertService.success(t("Loan-Added", { loanRef: values.loanRef }));
    });
  };

  const reportingYear = rows[0]?.snapshots[0]?.year ?? new Date().getFullYear();

  const borrowerOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.borrower))).sort(),
    [rows],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const matchSearch =
        !q ||
        r.loanRef.toLowerCase().includes(q) ||
        r.borrower.toLowerCase().includes(q);
      const matchAsset = assetClass === "all" || r.assetClass === assetClass;
      const matchStatus = status === "all" || r.status === status;
      const matchBorrower = borrower === "all" || r.borrower === borrower;
      return matchSearch && matchAsset && matchStatus && matchBorrower;
    });
  }, [rows, search, assetClass, status, borrower]);

  const onFilter =
    <T,>(setter: (v: T) => void) =>
    (v: T) => {
      setter(v);
      setPage(0);
    };

  const pageCount = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = filtered.slice(
    safePage * rowsPerPage,
    safePage * rowsPerPage + rowsPerPage,
  );

  return (
    <Box
      bgcolor="var(--white)"
      border="1px solid var(--gray5)"
      borderRadius="8px"
      p="16px"
      display="flex"
      flexDirection="column"
      gap="16px"
    >
      {/* Filter bar — Figma node 6108:20031 */}
      <Box display="flex" alignItems="center" flexWrap="wrap" gap="12px">
        <TextField
          size="small"
          placeholder={t("Search-Loan")}
          value={search}
          onChange={(e) => onFilter(setSearch)(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: "260px", flex: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: "150px" }}>
          <InputLabel id="all-loans-asset">{t("Filter-Asset-Class")}</InputLabel>
          <Select
            labelId="all-loans-asset"
            label={t("Filter-Asset-Class")}
            value={assetClass}
            onChange={(e) => onFilter(setAssetClass)(e.target.value)}
          >
            <MenuItem value="all">{t("Filter-All")}</MenuItem>
            <MenuItem value="business">{t("Asset-Business-Loans")}</MenuItem>
            <MenuItem value="listed">{t("Asset-Listed-Equity")}</MenuItem>
            <MenuItem value="project">{t("Asset-Project-Finance")}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: "130px" }}>
          <InputLabel id="all-loans-status">{t("Filter-Status")}</InputLabel>
          <Select
            labelId="all-loans-status"
            label={t("Filter-Status")}
            value={status}
            onChange={(e) => onFilter(setStatus)(e.target.value)}
          >
            <MenuItem value="all">{t("Filter-All")}</MenuItem>
            <MenuItem value="active">{t("Status-Active")}</MenuItem>
            <MenuItem value="closed">{t("Status-Closed")}</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: "150px" }}>
          <InputLabel id="all-loans-borrower">{t("Col-Borrower")}</InputLabel>
          <Select
            labelId="all-loans-borrower"
            label={t("Col-Borrower")}
            value={borrower}
            onChange={(e) => onFilter(setBorrower)(e.target.value)}
          >
            <MenuItem value="all">{t("Filter-All")}</MenuItem>
            {borrowerOptions.map((b) => (
              <MenuItem key={b} value={b}>
                {b}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="green"
          startIcon={<AddIcon />}
          onClick={() => setOpenAdd(true)}
          sx={{ padding: "8px 12px" }}
        >
          {t("Add-Loan")}
        </Button>
      </Box>

      <FeTableWrap>
        <FeTableScroll>
          <FeTable>
            <TableHead>
              <TableRow>
                <FeHeadCell>{t("Col-Loan-Ref")}</FeHeadCell>
                <FeHeadCell>{t("Col-Borrower")}</FeHeadCell>
                <FeHeadCell>{t("Col-Asset-Class")}</FeHeadCell>
                <FeHeadCell align="right">{t("Col-Principal")}</FeHeadCell>
                <FeHeadCell align="right">{t("Col-Outstanding")}</FeHeadCell>
                <FeHeadCell>{t("Col-Origination")}</FeHeadCell>
                <FeHeadCell align="center">{t("Col-Status")}</FeHeadCell>
                <FeHeadCell align="right">
                  {t("Col-tCO2e", { year: reportingYear })}
                </FeHeadCell>
                <FeHeadCell align="center">{t("DQS")}</FeHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((loan) => (
                <FeRow key={loan.id}>
                  <FeCell>{loan.loanRef}</FeCell>
                  <FeCell>{loan.borrower}</FeCell>
                  <FeCell>
                    <AssetClassChip assetClass={loan.assetClass} />
                  </FeCell>
                  <FeCell align="right">{formatNumber(loan.principal)}</FeCell>
                  <FeCell align="right">
                    {formatNumber(loan.outstanding)}
                  </FeCell>
                  <FeCell>{formatDate(loan.origination)}</FeCell>
                  <FeCell align="center">
                    <LoanStatusChip status={loan.status} />
                  </FeCell>
                  <FeCell align="right">
                    {loan.dqs === "excluded" ? "—" : formatNumber(loan.tco2e)}
                  </FeCell>
                  <FeCell align="center">
                    <DqsBadge level={loan.dqs} />
                  </FeCell>
                </FeRow>
              ))}
              {filtered.length === 0 && (
                <FeRow>
                  <FeCell colSpan={9} align="center" sx={{ py: "32px" }}>
                    <Typography variant="body2" color="var(--gray1)">
                      {t("noRowsLabel")}
                    </Typography>
                  </FeCell>
                </FeRow>
              )}
            </TableBody>
          </FeTable>
        </FeTableScroll>
        <FePagination
          count={filtered.length}
          page={safePage}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(n) => {
            setRowsPerPage(n);
            setPage(0);
          }}
        />
      </FeTableWrap>

      <AddLoanModal
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        borrowers={borrowerList.map((b) => ({ id: b.id, name: b.name }))}
        onSubmit={handleAddLoan}
      />
    </Box>
  );
};

export default AllLoans;
